import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Flame, Check, Clock } from "lucide-react";
import { SilhouetteVehicle } from "./SilhouetteVehicle";
import { WorkflowBar13 } from "./WorkflowBar13";

/**
 * VehicleCard — carte cockpit pour la Liste véhicules.
 *
 * "Chaque carte doit permettre au commercial de savoir en moins de 3 secondes
 *  quel véhicule nécessite une action, où il en est dans son workflow et
 *  quelle sera sa prochaine étape."
 *
 * Répartition : ~40 % photo / ~60 % information.
 * La priorité, la progression et la prochaine action DOMINENT la carte.
 * La photo est un repère visuel, jamais l'élément principal.
 */
export function VehicleCard({
  vehicle,
  index = 0,
  testid,
}) {
  const {
    name,
    plate,
    client,
    image = null,
    silhouette = "berline",
    completed = 5,
    priority = null, // numeric score (0-100) or null
    priorityLabel = null, // ex: "Urgence élevée"
    state = "avenir", // "action" | "encours" | "avenir" | "termine"
    nextAction = null, // ex: "Signature restante"
    nextActionUrgent = false,
    nextActionDone = false,
    orderNumber = null,
  } = vehicle;

  const isAction = state === "action";
  const isTermine = state === "termine";

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.04 * index,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -3 }}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-[22px] transition-shadow duration-300 ${
        isAction
          ? "bg-white shadow-[0_1px_2px_rgba(21,21,21,0.05),0_20px_48px_-20px_rgba(200,255,47,0.35),inset_0_0_0_1px_rgba(200,255,47,0.3)]"
          : isTermine
            ? "bg-white/60 shadow-none"
            : "bg-white shadow-wyvio-elev hover:shadow-wyvio-elev-hover"
      }`}
      style={
        isTermine
          ? { boxShadow: "inset 0 0 0 1px #E8E8E2" }
          : undefined
      }
    >
      {/* Photo / silhouette — ~40 % */}
      <div
        className={`relative w-full overflow-hidden ${
          isTermine ? "opacity-50" : ""
        }`}
        style={{ aspectRatio: "16 / 9" }}
      >
        {image ? (
          <img
            src={image}
            alt={name}
            className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]"
          />
        ) : (
          <SilhouetteVehicle
            type={silhouette}
            variant="dark"
            className="h-full w-full"
          />
        )}

        {/* Overlay léger pour lisibilité chip */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(21,21,21,0.20) 0%, rgba(21,21,21,0) 40%)",
          }}
        />

        {/* Badge priorité top-left (seulement si state=action) */}
        {isAction && priority != null && (
          <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-[#C8FF2F] px-2.5 py-1.5">
            <Flame className="h-3 w-3 text-[#151515]" strokeWidth={2.4} />
            <span className="wyvio-tabular text-[11px] font-medium tracking-[-0.005em] text-[#151515]">
              {priority}
            </span>
          </div>
        )}

        {/* Chip statut top-right */}
        <div className="absolute right-4 top-4">
          {isAction && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#151515] px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-white">
              <span
                aria-hidden
                className="wyvio-segment-active h-1.5 w-1.5 rounded-full"
                style={{ background: "#C8FF2F" }}
              />
              À traiter
            </span>
          )}
          {state === "encours" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-white backdrop-blur">
              En cours
            </span>
          )}
          {state === "avenir" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-white backdrop-blur">
              À venir
            </span>
          )}
          {isTermine && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-white backdrop-blur">
              <Check className="h-3 w-3" strokeWidth={2.4} />
              Livré
            </span>
          )}
        </div>

        {/* Flèche révélée au hover */}
        {!isTermine && (
          <span
            aria-hidden
            className="absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 opacity-0 shadow-[0_4px_12px_rgba(21,21,21,0.15)] transition-opacity duration-300 group-hover:opacity-100"
          >
            <ArrowUpRight
              className="h-4 w-4 text-[#151515] transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          </span>
        )}
      </div>

      {/* Bloc information — ~60 % */}
      <div className={`p-5 ${isTermine ? "opacity-60" : ""}`}>
        {/* Nom + plaque + client */}
        <div>
          <p className="text-[19px] font-medium leading-[1.1] tracking-[-0.018em] text-[#151515]">
            {name}
          </p>
          <div className="mt-2 flex items-center gap-2 text-[12.5px]">
            {plate && (
              <span className="wyvio-tabular text-[11px] font-medium uppercase tracking-[0.12em] text-[#8A8A80]">
                {plate}
              </span>
            )}
            {plate && client && (
              <span
                aria-hidden
                className="inline-block h-1 w-1 rounded-full bg-[#D9D9D2]"
              />
            )}
            {client && <span className="text-[#6B6B67]">{client}</span>}
          </div>
        </div>

        {/* Workflow bar + étape */}
        <div className="mt-5">
          <WorkflowBar13
            completed={completed}
            variant={isAction ? "lime" : "soft"}
            compact
          />
          <div className="mt-2.5 flex items-center justify-between">
            <span className="text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#8A8A80]">
              Étape {completed} / 13
            </span>
            {priorityLabel && isAction && (
              <span className="text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#151515]">
                {priorityLabel}
              </span>
            )}
          </div>
        </div>

        {/* Prochaine action */}
        {nextAction && (
          <div
            className={`mt-5 flex items-center gap-2 rounded-[12px] px-3 py-2.5 ${
              nextActionUrgent
                ? "bg-[#F4EEED]"
                : nextActionDone
                  ? "bg-[#F2F2EE]"
                  : "bg-[#F2F2EE]"
            }`}
          >
            {nextActionDone ? (
              <Check className="h-3.5 w-3.5 text-[#151515]" strokeWidth={2.4} />
            ) : nextActionUrgent ? (
              <Clock className="h-3.5 w-3.5 text-[#B7292E]" strokeWidth={2} />
            ) : (
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full bg-[#151515]"
              />
            )}
            <span
              className={`text-[12.5px] font-medium tracking-[-0.005em] ${
                nextActionUrgent
                  ? "text-[#B7292E]"
                  : nextActionDone
                    ? "text-[#151515]"
                    : "text-[#151515]"
              }`}
            >
              {nextAction}
            </span>
          </div>
        )}

        {/* Bon de commande */}
        {orderNumber && (
          <div className="mt-4 flex items-center justify-between border-t border-[#E8E8E2] pt-3">
            <span className="wyvio-tabular text-[10.5px] font-medium uppercase tracking-[0.18em] text-[#8A8A80]">
              {orderNumber}
            </span>
          </div>
        )}
      </div>
    </motion.article>
  );
}
